import React, { Component } from "react";

import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";

import { getWithExpiry, setWithExpiry } from "../../../helpers/storage";
import { logError } from "../../../helpers/logging";
import { reloadPage } from "../../../helpers/navigation";
import urlPatterns from "../../../urls";

// Failed lazy chunk loads (a content-hashed chunk from a previous deploy that
// no longer exists) are expected around deploys and are recovered by a one-off
// reload, so they are NOT reported to Sentry. Each browser/bundler phrases them
// differently, so we match all known variants.
// https://mitchgavan.com/code-splitting-react-safely/
const CHUNK_FAILED_MESSAGE = new RegExp(
  [
    // webpack runtime (legacy CRA builds) — browser-agnostic
    "Loading chunk [\\d]+ failed",
    // Chromium/Blink (Chrome, Edge, Android, in-app webviews)
    "Failed to fetch dynamically imported module",
    // Firefox/Gecko
    "error loading dynamically imported module",
    // Safari/WebKit — when the fallback responds 200 with text/html
    "is not a valid JavaScript MIME type",
    // Safari/WebKit — generic module load failure (e.g. network/404)
    "Importing a module script failed",
    // Vite's preload helper — a CSS dependency of a lazy chunk failed to load
    "Unable to preload CSS",
  ].join("|"),
  "i"
);

const devPanelStyles = {
  wrap: { padding: "24px", fontFamily: "monospace", color: "#7f1d1d" },
  pre: { whiteSpace: "pre-wrap", background: "#fef2f2", padding: "12px", overflowX: "auto" },
  button: { marginRight: "8px", padding: "4px 12px", font: "inherit", cursor: "pointer" },
};

class AppErrorBoundary extends Component {
  static propTypes = {
    children: PropTypes.node,
    history: PropTypes.shape({}),
  };

  static defaultProps = {
    children: null,
    history: {},
  };

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Errors caught by a boundary do NOT reach Sentry's global window.onerror
    // handler, so without this they are invisible in Sentry — the boundary
    // swallows them and (below) redirects to /404. Report everything except
    // expected deploy chunk-load failures (recovered by reload).
    if (!(error?.message && CHUNK_FAILED_MESSAGE.test(error.message))) {
      logError(error, { location: "AppErrorBoundary", componentStack: errorInfo?.componentStack });
    }
  }

  render() {
    const { hasError, error } = this.state;
    const { children, history } = this.props;
    const { location } = history || {};
    const { pathname } = location || {};
    /* Evaluated per render (not module scope) so tests can exercise both
       branches. Vite statically replaces process.env.NODE_ENV. */
    const isDev = process.env.NODE_ENV === "development";
    if (hasError && isDev) {
      /* In dev, redirecting to /404 or hard-reloading hides the real failure —
         an HMR hiccup or a genuine render error would silently "navigate" you
         to the not-found page. Show the error where it happened instead; the
         URL is untouched, so a fix + HMR (or Try again) resumes in place. */
      return (
        <div style={devPanelStyles.wrap}>
          <h2>Render error (dev only — production would show the 404 page)</h2>
          <pre style={devPanelStyles.pre}>{error?.stack || error?.message || String(error)}</pre>
          <button
            type="button"
            style={devPanelStyles.button}
            onClick={() => this.setState({ hasError: false, error: "" })}
          >
            Try again
          </button>
          <button type="button" style={devPanelStyles.button} onClick={() => reloadPage()}>
            Reload
          </button>
        </div>
      );
    }
    if (hasError) {
      if (
        error &&
        error.message &&
        CHUNK_FAILED_MESSAGE.test(error.message) &&
        !getWithExpiry("chunk_failed")
      ) {
        // This flag is used to avoid reloading the page if it was already reloaded in the last 10 seconds
        // because of a missing chunk. This is to avoid an infinite reload loop in the case were a chunk is
        // legitimately missing (not just missing because of a new deployment)
        setWithExpiry("chunk_failed", true, 10000);
        reloadPage();
        /* The page is reloading — rendering the broken children again here
           would just re-throw into an already-failed boundary, which React
           gives up on and lets the error escape. */
        return null;
      }
      if (pathname !== urlPatterns.NOT_FOUND) {
        /* Navigation belongs to <Redirect> alone. The previous history.push()
           here ran during render — a side effect React 18 aborts the render
           pass for, which let the original error escape the boundary. */
        return (
          <Redirect
            push
            to={{
              pathname: urlPatterns.NOT_FOUND,
              state: { isOldVersion: true },
            }}
          />
        );
      }
    }

    return children;
  }
}

export default AppErrorBoundary;
